import { Component, Input, Output, EventEmitter, ViewChild, inject } from '@angular/core';

import { StrategicPillarSelectorComponent } from '../strategic-pillar-selector/strategic-pillar-selector.component';
import { AudienceSelectorComponent } from '../audience-selector/audience-selector.component';
import { EntityType, StrategicPillar } from '../../services/strategic-pillar.service';
import { VeevaTaggedAudience } from '../../services/audience.service';
import { FeatureService } from '../../services/feature.service';

@Component({
  selector: 'app-pillars-audiences-section',
  standalone: true,
  imports: [StrategicPillarSelectorComponent, AudienceSelectorComponent],
  template: `
    <section class="pillars-audiences-section">
      <div class="field-group">
        @if (features.isEnabled('strategicPillars')) {
          <div class="field-row">
            <label class="rds-form-field__label">Strategic Pillars</label>
            <div class="field-control">
              <app-strategic-pillar-selector
                [entityType]="entityType"
                [entityId]="entityId"
                [isViewer]="isViewer"
                [initialPillars]="initialPillars"
                (pillarChanged)="onPillarChanged($event)">
              </app-strategic-pillar-selector>
            </div>
          </div>
        }
        @if (features.isEnabled('audiences')) {
          <div class="field-row">
            <label class="rds-form-field__label">Audiences</label>
            <div class="field-control">
              <app-audience-selector
                [entityType]="entityType"
                [entityId]="entityId"
                [isViewer]="isViewer"
                [initialAudiences]="initialAudiences"
                (audienceChanged)="onAudienceChanged($event)">
              </app-audience-selector>
            </div>
          </div>
        }
      </div>
    </section>
  `,
  styles: [`
    @use '@rds/core/styles/abstracts/functions' as f;
    .pillars-audiences-section { 
      margin-block: f.rds-spacing(6); 
      display: flex; 
      flex-direction: column;
      gap: f.rds-spacing(4);
    }
  .section-heading { display:none; }
    .field-group { display: flex; flex-direction: column; gap: 48px; }
    .field-row { display: flex; align-items: flex-start; gap: f.rds-spacing(6); }
    .field-row > label { 
      width: 160px; 
      padding-top: f.rds-spacing(2); 
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      text-transform: none;
      letter-spacing: 0.2px;
      font-family: roche-sans;
      -webkit-font-smoothing: antialiased;
      color: var(--rds-color-text-primary);
    }
    .field-control { flex: 1; min-width: 0; }

    /* Ensure inner selectors stretch full width */
    ::ng-deep app-strategic-pillar-selector rds-form-field,
    ::ng-deep app-audience-selector rds-form-field { width: 100%; }

    @media (max-width: 640px){
      .field-row { flex-direction: column; gap: f.rds-spacing(2); }
      .field-row > label { width: auto; padding-top: 0; }
    }
  `]
})
export class PillarsAudiencesSectionComponent {
  readonly features = inject(FeatureService);

  @Input() entityType!: EntityType;
  @Input() entityId!: string;
  @Input() isViewer: boolean = false;
  @Input() initialPillars: StrategicPillar[] = [];
  @Input() initialAudiences: VeevaTaggedAudience[] = [];
  @Output() pillarChanged = new EventEmitter<{ pillarIds: number[], pillars: any[] }>();
  @Output() audienceChanged = new EventEmitter<{ audienceIds: number[], audiences: any[] }>();
  
  @ViewChild(StrategicPillarSelectorComponent) pillarSelector!: StrategicPillarSelectorComponent;
  @ViewChild(AudienceSelectorComponent) audienceSelector!: AudienceSelectorComponent;

  onPillarChanged(evt: { pillarIds: number[], pillars: any[] }) { this.pillarChanged.emit(evt); }
  onAudienceChanged(evt: { audienceIds: number[], audiences: any[] }) { this.audienceChanged.emit(evt); }
  
  closeAllDropdowns() {
    this.pillarSelector?.closeDropdown();
    this.audienceSelector?.closeDropdown();
  }
}
